import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Mail, Lock, User, MapPin, Calendar, ArrowLeft, Eye, EyeOff, Coffee, Wind, Phone, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import PolicyModal from '../components/PolicyModal';
import { TERMS_AND_CONDITIONS, PRIVACY_POLICY, COOKIES_POLICY } from '../data/policies';

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    full_name: '',
    age: '',
    date_of_birth: '',
    location: '',
    mobile_number: '',
    looking_for: 'Man looking for women', // Default selection
    bio: '',
    openness_level: '',
    interests: [],
    relationship_status: '',
    looking_for_description: '',
    preferred_body_type: '',
    date_someone_with_children: ''
  });
  const [calculatedAge, setCalculatedAge] = useState(null);
  const [isUnderAge, setIsUnderAge] = useState(false);
  
  // Policy Agreement States
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToCookies, setAgreedToCookies] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  
  // Password Reset States
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: mobile, 3: captcha, 4: new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetMobileNumber, setResetMobileNumber] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [userCaptchaAnswer, setUserCaptchaAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 2-Step Verification States
  const [showVerification, setShowVerification] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState('');
  const [verificationMobile, setVerificationMobile] = useState('');
  const [verificationOtp, setVerificationOtp] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [requiresOtp, setRequiresOtp] = useState(false);

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operations = ['+', '-', '×'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let answer;
    let question;
    
    if (operation === '+') {
      answer = num1 + num2;
      question = `${num1} + ${num2}`;
    } else if (operation === '-') {
      // Ensure positive result
      const larger = Math.max(num1, num2);
      const smaller = Math.min(num1, num2);
      answer = larger - smaller;
      question = `${larger} - ${smaller}`;
    } else { // multiplication
      // Use smaller numbers for multiplication
      const small1 = Math.floor(Math.random() * 5) + 1;
      const small2 = Math.floor(Math.random() * 5) + 1;
      answer = small1 * small2;
      question = `${small1} × ${small2}`;
    }
    
    setCaptchaQuestion(question);
    setCaptchaAnswer(answer.toString());
  };

  const interests = [
    'Art Therapy', 'Cooking', 'Events', 'Exercise', 'Fine Dining', 'Journaling', 
    'Meditation', 'Movies', 'Music', 'Nature Walks', 'Photography', 'Reading', 
    'Restaurants', 'Sports', 'Technology', 'Theatre', 'Volunteering', 'Yoga'
  ];

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    
    const today = new Date();
    const birth = new Date(birthDate + 'T00:00:00'); // Ensure consistent timezone handling
    
    // More precise age calculation
    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    const dayDifference = today.getDate() - birth.getDate();
    
    // If the birthday hasn't occurred this year yet, subtract 1
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }
    
    return Math.max(0, age);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Handle date of birth changes
    if (name === 'date_of_birth') {
      const age = calculateAge(value);
      setCalculatedAge(age);
      setIsUnderAge(age !== null && age < 18);
      
      // Update age in formData for backend compatibility
      setFormData(prev => ({ ...prev, age: age || '' }));
    }
  };

  const handleCheckboxChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Policy agreement validation for registration
      if (!isLogin) {
        if (!agreedToTerms || !agreedToPrivacy || !agreedToCookies) {
          toast.error('You must agree to all policies to create an account');
          setLoading(false);
          return;
        }
      }

      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      let payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { ...formData, age: parseInt(formData.age) };

      // Age validation for registration
      if (!isLogin && (isUnderAge || !calculatedAge || calculatedAge < 18)) {
        toast.error('You must be 18 or older to join eternitysingles®');
        setLoading(false);
        return;
      }

      const response = await axios.post(endpoint, payload);
      
      // Check if 2-step verification is required
      if (response.data.requires_verification) {
        setVerificationSessionId(response.data.session_id);
        setRequiresOtp(!!response.data.requires_otp);
        setMaskedMobile(response.data.masked_mobile || '****');
        setMaskedEmail(response.data.masked_email || '***@***');
        setVerificationMobile('');
        setVerificationOtp('');
        setShowVerification(true);
        toast.info(response.data.requires_otp ? 'Verification code sent to your email' : 'Please verify your mobile number');
      } else {
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
        onLogin(response.data.access_token, response.data.user);
      }
    } catch (error) {
      const message = error.response?.data?.detail || (isLogin ? 'Login failed' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (requiresOtp && !verificationOtp.trim()) {
      toast.error('Please enter the verification code from your email');
      return;
    }
    if (!verificationMobile.trim()) {
      toast.error('Please enter your mobile number');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/auth/verify-2fa', {
        session_id: verificationSessionId,
        mobile_number: verificationMobile,
        otp: requiresOtp ? verificationOtp : ''
      });
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      setShowVerification(false);
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await axios.post('/auth/resend-otp', { session_id: verificationSessionId });
      toast.success('New verification code sent to your email');
    } catch (error) {
      toast.error('Failed to resend code. Please try logging in again.');
    }
  };

  const openPolicyModal = (policyType) => {
    setCurrentPolicy(policyType);
    setShowPolicyModal(true);
  };

  // Password Reset Handlers
  const handleForgotPassword = () => {
    setShowPasswordReset(true);
    setResetStep(1);
    setResetEmail('');
    setUserCaptchaAnswer('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleResetStep1 = () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    if (!resetEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setResetStep(2);
  };

  const handleResetStep2 = async () => {
    if (!resetMobileNumber) {
      toast.error('Please enter your mobile number');
      return;
    }
    if (resetMobileNumber.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/auth/verify-reset-mobile', {
        email: resetEmail,
        mobile_number: resetMobileNumber
      });
      generateCaptcha();
      setResetStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mobile number verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetStep3 = () => {
    if (userCaptchaAnswer !== captchaAnswer) {
      toast.error('Incorrect answer. Please try again.');
      setUserCaptchaAnswer('');
      generateCaptcha();
      return;
    }
    setResetStep(4);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/auth/reset-password', {
        email: resetEmail,
        mobile_number: resetMobileNumber,
        new_password: newPassword,
        captcha: userCaptchaAnswer
      });
      
      toast.success('Password reset successfully! You can now login with your new password.');
      setShowPasswordReset(false);
      setResetStep(1);
      // Clear reset form
      setResetEmail('');
      setResetMobileNumber('');
      setUserCaptchaAnswer('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Password reset failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const closePasswordReset = () => {
    setShowPasswordReset(false);
    setResetStep(1);
    setResetEmail('');
    setResetMobileNumber('');
    setUserCaptchaAnswer('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 text-emerald-600 hover:text-emerald-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-emerald-800" style={{fontFamily: 'Montserrat'}}>
            {isLogin ? 'Welcome Back to eternitysingles®' : 'Join the eternitysingles® Community'}
          </h2>
          <p className="mt-2 text-emerald-600">
            {isLogin ? 'Continue your wellness journey' : 'Start your journey to mindful connections'}
          </p>
        </div>

        {/* Form */}
        <div className="card max-w-lg mx-auto">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Toggle Login/Register */}
            <div className="flex bg-emerald-50 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  isLogin 
                    ? 'bg-white text-emerald-700 shadow-sm' 
                    : 'text-emerald-600 hover:text-emerald-700'
                }`}
                data-testid="login-tab"
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  !isLogin 
                    ? 'bg-white text-emerald-700 shadow-sm' 
                    : 'text-emerald-600 hover:text-emerald-700'
                }`}
                data-testid="register-tab"
              >
                Sign Up
              </button>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                <input
                  type="email"
                  name="email"
                  required
                  className="form-input pl-10"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  className="form-input pl-10 pr-10"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-emerald-500 hover:text-emerald-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Registration Fields */}
            {!isLogin && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="text"
                        name="username"
                        required
                        className="form-input pl-10"
                        placeholder="Choose a username"
                        value={formData.username}
                        onChange={handleInputChange}
                        data-testid="username-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      className="form-input"
                      placeholder="Your full name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      data-testid="fullname-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Date of Birth
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="date"
                        name="date_of_birth"
                        required
                        className={`form-input pl-10 ${isUnderAge ? 'border-red-500 text-red-600' : ''}`}
                        placeholder="YYYY-MM-DD"
                        value={formData.date_of_birth}
                        onChange={handleInputChange}
                        data-testid="date-of-birth-input"
                      />
                    </div>
                    {calculatedAge !== null && (
                      <div className={`mt-2 text-sm ${isUnderAge ? 'text-red-600' : 'text-emerald-600'}`}>
                        {isUnderAge ? (
                          <span className="font-semibold">You are {calculatedAge} years old (Under 18)</span>
                        ) : (
                          <span>You are {calculatedAge} years old</span>
                        )}
                      </div>
                    )}
                    {isUnderAge && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="font-semibold mb-1">⚠️ Age Restriction Notice:</p>
                        <p>If you are under the age of 18, kindly refrain from utilising this app. This app is exclusively for adults over the age of 18. Any individual found using a fraudulent profile will be swiftly terminated.</p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="text"
                        name="location"
                        required
                        className="form-input pl-10"
                        placeholder="City, State"
                        value={formData.location}
                        onChange={handleInputChange}
                        data-testid="location-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Mobile Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="tel"
                        name="mobile_number"
                        required
                        className="form-input pl-10"
                        placeholder="Your mobile number"
                        value={formData.mobile_number}
                        onChange={handleInputChange}
                        data-testid="mobile-number-input"
                      />
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">Required for account recovery</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      I'm a
                    </label>
                    <div className="relative">
                      <Heart className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <select
                        name="looking_for"
                        required
                        className="form-input pl-10 appearance-none"
                        value={formData.looking_for}
                        onChange={handleInputChange}
                        data-testid="looking-for-select"
                      >
                        <option value="Man looking for women">Man looking for women</option>
                        <option value="Woman looking for men">Woman looking for men</option>
                        <option value="Man looking for men">Man looking for men</option>
                        <option value="Woman looking for women">Woman looking for women</option>
                      </select>
                      {/* Custom dropdown arrow */}
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Relationship Status */}
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Relationship Status
                    </label>
                    <div className="relative">
                      <Heart className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <select
                        name="relationship_status"
                        className="form-input pl-10 appearance-none"
                        value={formData.relationship_status}
                        onChange={handleInputChange}
                      >
                        <option value="">Choose status</option>
                        <option value="single_looking">Single</option>
                        <option value="separated">Separated</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                      {/* Custom dropdown arrow */}
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Tell us about yourself
                  </label>
                  <textarea
                    name="bio"
                    className="form-input min-h-[100px] resize-none"
                    placeholder="Share a bit about your journey and what you're looking for..."
                    value={formData.bio}
                    onChange={handleInputChange}
                    maxLength="1000"
                    data-testid="bio-input"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-emerald-600">Tell your story and share your journey</span>
                    <span className="text-xs text-emerald-600">
                      {formData.bio.length}/1000 words
                    </span>
                  </div>
                </div>

                {/* What am I looking for */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    What am I looking for
                  </label>
                  <textarea
                    name="looking_for_description"
                    className="form-input min-h-[120px] resize-none"
                    placeholder="Describe what you're hoping to find in a connection, relationship, or support system..."
                    value={formData.looking_for_description}
                    onChange={handleInputChange}
                    maxLength="750"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-emerald-600">Share your hopes and what you're seeking</span>
                    <span className="text-xs text-emerald-600">
                      {formData.looking_for_description.length}/750 words
                    </span>
                  </div>
                </div>

                {/* Partner Preferences */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Body type I'm looking for
                  </label>
                  <div className="relative">
                    <Heart className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                    <select
                      name="preferred_body_type"
                      className="form-input pl-10 appearance-none"
                      value={formData.preferred_body_type}
                      onChange={handleInputChange}
                    >
                      <option value="">Select (optional)</option>
                      <option value="slim">Slim/Slender</option>
                      <option value="average">Average</option>
                      <option value="athletic">Athletic/Fit</option>
                      <option value="curvy">Curvy</option>
                      <option value="plus_size">Plus-size</option>
                      <option value="muscular">Muscular</option>
                      <option value="any">Any/No preference</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Children Preference */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Do you want to date someone with children?
                  </label>
                  <div className="relative">
                    <Heart className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                    <select
                      name="date_someone_with_children"
                      className="form-input pl-10 appearance-none"
                      value={formData.date_someone_with_children}
                      onChange={handleInputChange}
                      data-testid="children-preference-select"
                    >
                      <option value="">Select (optional)</option>
                      <option value="yes">Yes, I'm open to it</option>
                      <option value="no">No, I prefer not to</option>
                      <option value="maybe">Maybe, depends on the situation</option>
                      <option value="no_preference">No preference</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Relationship Goals section removed - now available only in Profile */}

                {/* Mental Health Goals section removed - now available only in Profile */}

                {/* Communication Style */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-2">
                    Communication Style
                  </label>
                  <select
                    name="openness_level"
                    className="form-input"
                    value={formData.openness_level}
                    onChange={handleInputChange}
                    data-testid="openness-level-select"
                  >
                    <option value="">Select...</option>
                    <option value="private">Private & Reserved</option>
                    <option value="open">Open & Sharing</option>
                    <option value="very_open">Very Open & Transparent</option>
                  </select>
                </div>

                {/* Interests */}
                <div>
                  <label className="block text-sm font-medium text-emerald-700 mb-3">
                    Interests & Hobbies
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {interests.map(interest => (
                      <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                          checked={formData.interests.includes(interest)}
                          onChange={() => handleCheckboxChange('interests', interest)}
                        />
                        <span className="text-sm text-emerald-700">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Policy Agreements - Only show on signup */}
            {!isLogin && (
              <div className="space-y-4 pt-4 border-t border-emerald-200">
                <p className="text-sm font-medium text-emerald-800 mb-3">
                  By creating an account, you agree to the following:
                </p>
                
                {/* Terms and Conditions */}
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    required
                  />
                  <span className="text-sm text-emerald-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => openPolicyModal('terms')}
                      className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                    >
                      Terms and Conditions
                    </button>
                  </span>
                </label>

                {/* Privacy Policy */}
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    required
                  />
                  <span className="text-sm text-emerald-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => openPolicyModal('privacy')}
                      className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </span>
                </label>

                {/* Cookies Policy */}
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreedToCookies}
                    onChange={(e) => setAgreedToCookies(e.target.checked)}
                    className="mt-1 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    required
                  />
                  <span className="text-sm text-emerald-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => openPolicyModal('cookies')}
                      className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                    >
                      Cookies Policy
                    </button>
                  </span>
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (!isLogin && isUnderAge) || (!isLogin && (!agreedToTerms || !agreedToPrivacy || !agreedToCookies))}
              className="btn-primary w-full py-3 text-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"}
            >
              {loading ? (
                <div className="spinner border-white"></div>
              ) : (
                <>
                  <Heart className="w-5 h-5" />
                  <span>{isLogin ? 'Login to eternitysingles®' : 'Join eternitysingles®'}</span>
                </>
              )}
            </button>

            {/* Forgot Password Link - only show on login */}
            {isLogin && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            )}
          </form>
        </div>

        {/* 2-Step Verification Modal */}
        {showVerification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-emerald-800" data-testid="2fa-title">2-Step Verification</h2>
                  <button
                    onClick={() => setShowVerification(false)}
                    className="text-gray-400 hover:text-gray-600"
                    data-testid="2fa-close-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4 p-3 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Shield className="w-5 h-5" />
                    <span className="text-sm font-medium">Verify Your Identity</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1">
                    {requiresOtp
                      ? <>A 6-digit code has been sent to <strong>{maskedEmail}</strong>. Enter it below along with your mobile number ending in <strong>{maskedMobile}</strong>.</>
                      : <>Enter your registered mobile number ending in <strong>{maskedMobile}</strong> to continue.</>
                    }
                  </p>
                </div>

                <form onSubmit={handleVerify2FA} className="space-y-4">
                  {requiresOtp && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Verification Code</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={verificationOtp}
                        onChange={(e) => setVerificationOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-center text-lg tracking-widest font-mono"
                        data-testid="2fa-otp-input"
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="tel"
                        value={verificationMobile}
                        onChange={(e) => setVerificationMobile(e.target.value)}
                        placeholder="Enter your mobile number"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        data-testid="2fa-mobile-input"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
                    data-testid="2fa-verify-btn"
                  >
                    {loading ? 'Verifying...' : 'Verify & Continue'}
                  </button>
                  {requiresOtp && (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="w-full py-2 px-4 text-emerald-600 bg-transparent hover:bg-emerald-50 rounded-lg transition-colors text-sm font-medium"
                    data-testid="2fa-resend-btn"
                  >
                    Didn't receive the code? Resend
                  </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-emerald-600 space-y-1">
          <p>By continuing, you agree to prioritise mental wellness and respectful connections.</p>
          <p className="font-medium">Must be 18+ years old to join eternitysingles®</p>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-emerald-800">Reset Password</h2>
                <button
                  onClick={closePasswordReset}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Step 1: Email Input */}
              {resetStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="form-input pl-10"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleResetStep1}
                    className="btn-primary w-full py-3"
                  >
                    Continue
                  </button>
                </div>
              )}

              {/* Step 2: Mobile Number Verification */}
              {resetStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Mobile Number
                    </label>
                    <p className="text-xs text-emerald-600 mb-2">
                      Enter the mobile number associated with your account
                    </p>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="tel"
                        value={resetMobileNumber}
                        onChange={(e) => setResetMobileNumber(e.target.value)}
                        className="form-input pl-10"
                        placeholder="Enter your mobile number"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(1)}
                      className="flex-1 py-3 px-4 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleResetStep2}
                      className="flex-1 btn-primary py-3"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Captcha Verification */}
              {resetStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Security Verification
                    </label>
                    <div className="bg-emerald-50 p-4 rounded-lg mb-3">
                      <p className="text-lg font-bold text-emerald-800 text-center">
                        What is {captchaQuestion} ?
                      </p>
                    </div>
                    <input
                      type="number"
                      value={userCaptchaAnswer}
                      onChange={(e) => setUserCaptchaAnswer(e.target.value)}
                      className="form-input text-center text-lg"
                      placeholder="Enter your answer"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(2)}
                      className="flex-1 py-3 px-4 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleResetStep3}
                      className="flex-1 btn-primary py-3"
                    >
                      Verify
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: New Password */}
              {resetStep === 4 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="form-input pl-10"
                        placeholder="Enter new password"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-emerald-500" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="form-input pl-10"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetStep(3)}
                      className="flex-1 py-3 px-4 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handlePasswordReset}
                      disabled={loading}
                      className="flex-1 btn-primary py-3 disabled:opacity-50"
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Policy Modal */}
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        title={
          currentPolicy === 'terms' ? 'Terms and Conditions' :
          currentPolicy === 'privacy' ? 'Privacy Policy' :
          'Cookies Policy'
        }
        content={
          currentPolicy === 'terms' ? TERMS_AND_CONDITIONS :
          currentPolicy === 'privacy' ? PRIVACY_POLICY :
          COOKIES_POLICY
        }
      />
    </div>
  );
};

export default AuthPage;